import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { playMenuOpenSound, playMenuCloseSound } from "@/lib/menuSound";
import {
  ChevronDown, LogOut, UserCircle2, Shield, LayoutGrid, Users, Gauge,
  FileText, BarChart3, Settings, Bell, Search, Truck, Map, PackageCheck, PackagePlus, CalendarDays,
  FilePlus2, ArrowUpRight, X, AlertTriangle, Info, Receipt,
  Trophy, ShoppingBag, Gem, Coins, ScrollText, Megaphone, Radar, FolderCheck,
} from "lucide-react";
import { TrackingBadge } from "@/components/LocationTracker";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { Notifications as NotificationsAPI } from "@/lib/api";
import ConsentGate from "@/components/ConsentGate";
import BroadcastGate from "@/components/BroadcastGate";
import XpBurst from "@/components/XpBurst";
import { toast } from "sonner";
import { BottomNav } from "@/components/BottomNav";
import { QuickSearch } from "@/components/QuickSearch";

const ROLE_LABELS = {
  admin: "Admin",
  koordynator: "Koordynator",
  ksiegowa: "Księgowa",
  kierowca: "Kierowca",
  tester: "Tester",
};

/**
 * Left sidebar rail — small icon-only shortcuts. Each item is RBAC-gated:
 * shown always, dimmed if the user lacks the permission, click surfaces toast.
 */
const RAIL_ITEMS = [
  { to: "/", icon: LayoutGrid, label: "Pulpit", perm: null, testId: "rail-home" },
  { to: "/employees", icon: Users, label: "Pracownicy", perm: "employees", testId: "rail-employees" },
  { to: "/fleet", icon: Gauge, label: "Flota", perm: "fleet", testId: "rail-fleet" },
  { to: "/contracts", icon: FileText, label: "Umowy", perm: "contracts", testId: "rail-contracts" },
  { to: "/finance", icon: BarChart3, label: "Finanse", perm: "finance", testId: "rail-finance" },
  { to: "/accounting", icon: Receipt, label: "Księgowość", perm: "finance", testId: "rail-accounting" },
];

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  // Two-phase menu state so the exit animation gets a chance to play:
  //   null → closed, "entering" → mounted+animating in, "exiting" → animating out.
  const [menuPhase, setMenuPhase] = useState(null);

  const openMenu = () => {
    playMenuOpenSound();
    setMenuPhase("entering");
  };
  const [searchOpen, setSearchOpen] = useState(false);
  const canSearch = user && user.role !== "kierowca";
  useEffect(() => {
    if (!canSearch) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSearch]);
  const closeMenu = () => {
    playMenuCloseSound();
    setMenuPhase("exiting");
    // Unmount after the exit animation finishes (matches CSS 0.22s).
    setTimeout(() => setMenuPhase(null), 260);
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex" data-testid="app-shell">
      {/* LEFT RAIL */}
      <aside
        className="hidden md:flex sticky top-0 h-screen w-16 shrink-0 flex-col items-center justify-between py-4 bg-white border-r border-zinc-200"
        data-testid="left-rail"
      >
        <div className="flex flex-col items-center gap-1.5">
          <Link
            to="/"
            data-testid="rail-brand"
            title="GoPossible — powrót do menu"
            className="w-11 h-11 rounded-xl overflow-hidden bg-white shadow-[0_6px_20px_-6px_rgba(229,44,52,0.6)] hover:scale-105 transition-transform mb-3 flex items-center justify-center"
          >
            <img
              src="/assets/gopossible-logo.png"
              alt="GoPossible"
              className="gp-logo w-full h-full object-contain"
            />
          </Link>
          {RAIL_ITEMS.map((item) => (
            <RailButton key={item.to} {...item} user={user} location={location} />
          ))}
        </div>
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            data-testid="rail-admin"
            title="Panel administratora"
            className={({ isActive }) =>
              `w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${
                isActive
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"
              }`
            }
          >
            <Settings className="w-5 h-5" />
          </NavLink>
        )}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* TOP HEADER */}
        <header
          data-testid="app-header"
          className="sticky top-0 z-40 bg-white border-b border-zinc-200"
        >
          <div className="px-3 sm:px-5 md:px-8 h-20 flex items-center justify-between gap-2 md:gap-4 min-w-0">
            {/* LEFT — brand lockup */}
            <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group shrink-0">
              <img
                src="/assets/gopossible-header.png"
                alt="GoPossible · Powered by Ambitions"
                className="gp-logo h-10 w-20 sm:w-36 md:h-14 md:w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.src = "/assets/gopossible-logo.png";
                }}
              />
            </Link>

            {/* CENTER — MENU APLIKACJI button */}
            <button
              type="button"
              onClick={openMenu}
              data-testid="btn-menu-aplikacji"
              aria-label="Menu aplikacji"
              className="flex-1 min-w-0 max-w-md inline-flex items-center gap-1.5 sm:gap-3 justify-center bg-brand hover:bg-brand-hover text-white h-12 px-2 sm:px-6 rounded-xl shadow-[0_8px_20px_-6px_rgba(229,44,52,0.55)] transition-colors hover:bg-brand-hover"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="font-heading font-black text-base uppercase tracking-wider">
                Menu<span className="hidden lg:inline"> aplikacji</span>
              </span>
            </button>

            {/* RIGHT — notifications + user */}
            <div className="flex items-center gap-2">
              {canSearch && (
                <button type="button" onClick={() => setSearchOpen(true)} data-testid="header-search-btn" aria-label="Szukaj (Ctrl+K)" title="Szukaj (Ctrl+K)" className="hidden md:inline-flex items-center gap-2 h-10 px-3 rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-900 hover:text-white transition-colors">
                  <Search className="w-4 h-4" /><kbd className="hidden lg:inline px-1.5 py-0.5 rounded bg-white ring-1 ring-zinc-300 text-[10px] font-mono font-bold text-zinc-700">Ctrl K</kbd>
                </button>
              )}
              <TrackingBadge />
              <NotificationsBell />
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-full bg-zinc-100 hover:bg-zinc-900 hover:text-white outline-none transition-colors"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center">
                        <UserCircle2 className="w-4 h-4" />
                      </div>
                    )}
                    <span className="hidden md:inline font-bold uppercase tracking-wider text-[11px]">
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                    <ChevronDown className="w-3 h-3" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="rounded-2xl border border-zinc-200 p-0 min-w-[240px] shadow-2xl overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
                      <div className="font-semibold text-sm text-zinc-900">
                        {user.name || (user.email || "").split("@")[0]}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5 truncate">
                        {user.email || user.username}
                      </div>
                    </div>
                    {user.role === "admin" && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-none py-3 px-4 cursor-pointer font-semibold uppercase text-xs tracking-wider border-b border-zinc-100 focus:bg-zinc-900 focus:text-white"
                      >
                        <NavLink to="/admin" data-testid="nav-admin">
                          <Shield className="w-4 h-4 mr-2 inline" />
                          Panel administratora
                        </NavLink>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={logout}
                      data-testid="btn-logout"
                      className="rounded-none py-3 px-4 cursor-pointer font-semibold uppercase text-xs tracking-wider text-brand focus:bg-brand focus:text-white"
                    >
                      <LogOut className="w-4 h-4 mr-2 inline" />
                      Wyloguj
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 px-3 sm:px-5 md:px-8 py-8 bg-zinc-50">
          <div key={location.pathname} className="page-enter"><Outlet /></div>
        </main>
        <BottomNav onMenu={openMenu} onSearch={() => setSearchOpen(true)} />
        {canSearch && <QuickSearch open={searchOpen} onClose={() => setSearchOpen(false)} />}

        <footer className="px-5 md:px-8 pt-6 pb-24 md:pb-6 border-t border-zinc-200 mt-8 text-[11px] font-mono text-zinc-500 flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-white">
          <span>© {new Date().getFullYear()} GoPossible Sp. z o.o. · NIP 8513329264</span>
          <span>Powered by Ambitions</span>
        </footer>
      </div>

      {menuPhase && <AppMenu user={user} phase={menuPhase} onClose={closeMenu} />}
      <ConsentGate />
      <BroadcastGate />
      <XpBurst />
    </div>
  );
}

function RailButton({ to, icon: Icon, label, perm, testId, user, location }) {
  const allowed = !perm || hasPermission(user, perm);
  const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));
  const cls = `w-11 h-11 flex items-center justify-center rounded-xl transition-all ${
    active ? "bg-brand text-white shadow-[0_6px_18px_-6px_rgba(229,44,52,0.55)]"
           : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
  } ${allowed ? "" : "opacity-40"}`;

  if (!allowed) {
    return (
      <button
        onClick={() => toast.error(`Nie masz uprawnień do „${label}".`)}
        title={label}
        data-testid={testId}
        className={cls}
      >
        <Icon className="w-5 h-5" />
      </button>
    );
  }
  return (
    <NavLink to={to} title={label} data-testid={testId} className={cls}>
      <Icon className="w-5 h-5" />
    </NavLink>
  );
}

/** App Menu overlay — big grid of gradient tiles (the previous launcher).
 *  Opens from the header button and closes to reveal the current section.
 *  Renders with staggered enter animations + Web-Audio whoosh on open.
 */
function AppMenu({ user, phase, onClose }) {
  const isExiting = phase === "exiting";
  const isDriver = user?.role === "kierowca";
  const TILES_ALL = [
    { to: "/fleet", label: "Flota", desc: "Pojazdy · GPS · Serwis", icon: Truck, perm: "fleet",
      gradient: "from-red-600 via-red-700 to-zinc-950", testId: "menu-tile-fleet" },
    { to: "/routes", label: "Trasy", desc: "Planer · Import KOP", icon: Map, perm: "routes",
      gradient: "from-indigo-500 via-indigo-700 to-slate-900", testId: "menu-tile-routes" },
    { to: "/mapa", label: "Mapa", desc: "Kurierzy na żywo · Ślad · Historia", icon: Radar, perm: "routes",
      gradient: "from-emerald-600 via-teal-800 to-zinc-950", testId: "menu-tile-courier-map" },
    { to: "/zlecenia", label: "Zlecenia", desc: "Swipe · Lista · Mapa · Rozliczenia", icon: PackageCheck, perm: "deliveries",
      gradient: "from-emerald-500 via-teal-700 to-emerald-950", testId: "menu-tile-deliveries" },
    { to: "/zaladunek", label: "Załadunek", desc: "Paczki · Miejsce na pace · Trasa", icon: PackagePlus, perm: null,
      gradient: "from-orange-500 via-red-700 to-zinc-950", testId: "menu-tile-loading" },
    { to: "/schedule", label: "Grafik", desc: "Tygodniowy plan kurierów", icon: CalendarDays, perm: "schedule",
      gradient: "from-amber-500 via-orange-600 to-red-800", testId: "menu-tile-schedule" },
    { to: "/finance", label: "Finanse", desc: "KUP · Wypłaty · Cashflow", icon: BarChart3, perm: "finance",
      gradient: "from-sky-500 via-blue-700 to-slate-900", testId: "menu-tile-finance" },
    { to: "/accounting", label: "Księgowość", desc: "Faktury · KSeF · UPO", icon: Receipt, perm: "finance",
      gradient: "from-lime-500 via-emerald-700 to-emerald-950", testId: "menu-tile-accounting" },
    { to: "/employees", label: "Pracownicy", desc: "Baza · Dane osobowe", icon: Users, perm: "employees",
      gradient: "from-pink-500 via-fuchsia-700 to-purple-950", testId: "menu-tile-employees" },
    { to: "/kadry", label: "Kadry", desc: "Teczki · Terminy · Rachunki", icon: FolderCheck, perm: "hr",
      gradient: "from-amber-500 via-orange-700 to-stone-950", testId: "menu-tile-hr" },
    { to: "/contracts/new", label: "Nowy dokument", desc: "Kreator umów", icon: FilePlus2, perm: "contracts",
      gradient: "from-violet-500 via-purple-700 to-indigo-950", testId: "menu-tile-new-contract" },
    { to: "/contracts", label: "Historia umów", desc: "Filtry · PDF · Archiwum", icon: FileText, perm: "contracts",
      gradient: "from-zinc-500 via-zinc-700 to-zinc-950", testId: "menu-tile-contracts" },
    // Gamification — dostępne dla WSZYSTKICH zalogowanych (kierowca głównie).
    { to: "/osiagniecia", label: "Osiągnięcia", desc: "Odznaki · XP · Bonusy",
      icon: Trophy, perm: null,
      gradient: "from-purple-500 via-fuchsia-700 to-zinc-950", testId: "menu-tile-achievements" },
    { to: "/sklep", label: "Sklep GoPossible", desc: "Wymień diamenty na nagrody",
      icon: ShoppingBag, perm: null,
      gradient: "from-cyan-500 via-blue-700 to-zinc-950", testId: "menu-tile-shop" },
    ...(user?.role === "admin"
      ? [{ to: "/admin/gamifikacja", label: "Gamifikacja", desc: "System RPG · Sklep · Zamówienia", icon: Gem, perm: "admin",
          gradient: "from-purple-700 via-fuchsia-900 to-black", testId: "menu-tile-gami-admin" },
         { to: "/admin/stawki", label: "Stawki kurierów", desc: "PLN/paczka · Indywidualne stawki", icon: Coins, perm: "admin",
          gradient: "from-emerald-600 via-teal-800 to-zinc-950", testId: "menu-tile-driver-rates" },
         { to: "/admin/zgody", label: "Zgody i regulaminy", desc: "RODO · Regulamin · Wersje", icon: ScrollText, perm: "admin",
          gradient: "from-slate-700 via-zinc-800 to-black", testId: "menu-tile-consents" },
         { to: "/admin/komunikaty", label: "Komunikaty", desc: "Powiadomienia · Broadcast", icon: Megaphone, perm: "admin",
          gradient: "from-orange-500 via-red-700 to-zinc-950", testId: "menu-tile-broadcasts" }]
      : []),
    ...(user?.role === "admin"
      ? [{ to: "/admin", label: "Panel admina", desc: "System · Sesje · Użytkownicy", icon: Shield, perm: "admin",
          gradient: "from-zinc-800 via-zinc-900 to-black", testId: "menu-tile-admin" }]
      : []),
  ];

  // For kierowca, only show driver-relevant apps (no admin/finance/routes greyed out).
  const DRIVER_TILE_IDS = new Set([
    "menu-tile-work",
    "menu-tile-my-route",
    "menu-tile-deliveries",
    "menu-tile-loading",
    "menu-tile-schedule",
    "menu-tile-achievements",
    "menu-tile-shop",
  ]);
  const TILES = isDriver
    ? TILES_ALL.filter((t) => DRIVER_TILE_IDS.has(t.testId))
    : TILES_ALL;

  // Close on ESC — accessibility win + feels native.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className={`fixed inset-0 z-[1300] flex items-start justify-center p-3 sm:p-6 overflow-y-auto bg-zinc-950/70 ${isExiting ? "menu-backdrop-exit" : "menu-backdrop-enter"}`}
      onClick={onClose}
      data-testid="app-menu-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Menu aplikacji"
    >
      <div
        className={`relative w-full max-w-6xl my-2 sm:my-8 ${isExiting ? "menu-panel-exit" : "menu-panel-enter"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="app-menu"
      >
        <div className={`flex items-center justify-between px-2 py-2 sm:py-4 mb-2 sm:mb-4 ${isExiting ? "" : "menu-header-enter"}`}>
          <div>
            <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-white/60 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
              // Menu aplikacji
            </div>
            <h2 className="font-heading font-black text-xl sm:text-3xl uppercase tracking-tight mt-1 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.75)]">
              Wybierz obszar
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="app-menu-close"
            aria-label="Zamknij menu"
            className="relative w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/10 backdrop-blur-xl ring-1 ring-white/20 text-white hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="p-1 sm:p-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 lg:gap-5">
          {TILES.map((t, i) => (
            <MenuTile
              key={t.to}
              user={user}
              onNavigate={onClose}
              stagger={isExiting ? 0 : 0.05 + i * 0.035}
              exiting={isExiting}
              {...t}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MenuTile({ to, label, desc, icon: Icon, perm, gradient, testId, user, onNavigate, stagger = 0, exiting = false }) {
  const allowed = !perm || hasPermission(user, perm);
  const base = "menu-tile-3d group relative overflow-hidden rounded-2xl sm:rounded-3xl min-h-[110px] sm:min-h-[160px] lg:min-h-[180px] p-3 sm:p-5 lg:p-6 flex flex-col justify-between text-white shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]";
  const enterCls = exiting ? "" : "menu-tile-enter";
  const cls = `${base} ${enterCls} bg-gradient-to-br ${gradient} ${allowed ? "" : "opacity-20 grayscale saturate-0 brightness-50 blur-[1.5px] cursor-not-allowed"}`;
  const style = exiting ? undefined : { animationDelay: `${stagger}s` };

  const inner = (
    <>
      <Icon aria-hidden className="pointer-events-none absolute -right-6 -bottom-8 w-36 h-36 sm:w-48 sm:h-48 opacity-[0.10] transition-all duration-500 group-hover:opacity-[0.16] group-hover:scale-110" strokeWidth={1.5} />
      <div className="relative flex items-start justify-between">
        <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center ring-1 ring-white/25">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
        </div>
        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/25 transition-colors">
          <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </div>
      </div>
      <div className="relative">
        <div className="font-heading font-black text-sm sm:text-xl md:text-2xl uppercase tracking-tight leading-none">
          {label}
        </div>
        <div className="hidden sm:block text-xs md:text-sm mt-2 text-white/80">{desc}</div>
      </div>
    </>
  );

  if (!allowed) {
    return (
      <div
        onClick={() => toast.error(`Nie masz uprawnień do „${label}".`)}
        data-testid={testId}
        className={cls}
        style={style}
        title="Brak uprawnień"
      >
        {/* Heavy dark overlay + backdrop-blur — makes it obvious the tile is
            locked while a faint colour hint stays visible underneath. */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-none rounded-2xl sm:rounded-3xl" />
        {inner}
        <div className="absolute top-3 right-3 z-10 text-[9px] font-mono uppercase tracking-widest bg-black/80 backdrop-blur-md text-white/90 rounded-full px-2 py-0.5 ring-1 ring-white/20 filter-none">
          Brak dostępu
        </div>
      </div>
    );
  }
  return (
    <Link to={to} onClick={onNavigate} data-testid={testId} className={cls} style={style}>
      {inner}
    </Link>
  );
}


/* ---------- Notifications bell (header) ---------- */
const TONE_STYLES = {
  danger: { dot: "bg-brand", pill: "bg-red-50 border-red-200 text-red-700", icon: AlertTriangle },
  warn:   { dot: "bg-amber-500", pill: "bg-amber-50 border-amber-200 text-amber-800", icon: AlertTriangle },
  info:   { dot: "bg-sky-500", pill: "bg-sky-50 border-sky-200 text-sky-800", icon: Info },
  ok:     { dot: "bg-emerald-500", pill: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: Info },
};

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    NotificationsAPI.list()
      .then((res) => setItems(res.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 60000);
    return () => clearInterval(iv);
  }, [refresh]);

  const dangerCount = items.filter((i) => i.tone === "danger").length;
  const total = items.length;

  const go = (href) => {
    setOpen(false);
    if (href) navigate(href);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="btn-notifications"
        title="Powiadomienia"
        className="relative w-11 h-11 flex items-center justify-center rounded-xl hover:bg-zinc-100 text-zinc-700 outline-none"
      >
        <Bell className="w-5 h-5" />
        {total > 0 && (
          <span
            className={`absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-white ${dangerCount > 0 ? "bg-brand" : "bg-amber-500"}`}
            data-testid="notif-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="rounded-2xl border border-zinc-200 p-0 min-w-[360px] max-w-[440px] shadow-2xl overflow-hidden"
        data-testid="notif-dropdown"
      >
        <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              // Powiadomienia
            </div>
            <div className="font-heading font-black text-base uppercase tracking-tight">
              {total > 0 ? `${total} ${total === 1 ? "alert" : total < 5 ? "alerty" : "alertów"}` : "Wszystko OK"}
            </div>
          </div>
          <button
            onClick={refresh}
            data-testid="notif-refresh"
            className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-brand"
          >
            Odśwież
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-zinc-100">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">
              Brak nowych powiadomień.
            </div>
          )}
          {items.map((it) => {
            const style = TONE_STYLES[it.tone] || TONE_STYLES.info;
            const Icon = style.icon;
            return (
              <button
                key={it.id}
                onClick={() => go(it.href)}
                data-testid={`notif-${it.kind}`}
                className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors flex items-start gap-3"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${style.pill} shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">
                    {it.label}
                  </div>
                  {it.hint && (
                    <div className="text-xs text-zinc-500 truncate mt-0.5">{it.hint}</div>
                  )}
                </div>
                <ArrowUpRight className="w-4 h-4 text-zinc-400 shrink-0 mt-1" />
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
